import type {
  ClinicalAnatomicalMapType,
} from "../../types";

export type AnatomicalTemplateView =
  | "FRONT"
  | "BACK"
  | "LEFT"
  | "RIGHT"
  | "THREE_QUARTER_LEFT"
  | "THREE_QUARTER_RIGHT"
  | "INFERIOR"
  | "SUPERIOR"
  | "LATERAL"
  | "POSTERIOR"
  | "ANTERIOR_OBLIQUE"
  | "POSTERIOR_OBLIQUE"
  | "FRONTAL_HAIRLINE"
  | "VERTEX"
  | "OCCIPITAL"
  | "TEMPORAL_LEFT"
  | "TEMPORAL_RIGHT"
  | "ANTERIOR_OBLIQUE_LEFT"
  | "ANTERIOR_OBLIQUE_RIGHT";

export type AnatomicalTemplateRegion =
  | "FULL_BODY"
  | "FACE"
  | "NOSE"
  | "EAR"
  | "NECK_DECOLLETE"
  | "CHEST_BREAST"
  | "ABDOMEN_FLANKS"
  | "GLUTEAL"
  | "ARMS"
  | "THIGHS_KNEES"
  | "SCALP";

export type AnatomicalTemplateSex =
  | "FEMALE"
  | "MALE"
  | "NEUTRAL";

export interface AnatomicalZone {
  code: string;
  label: string;
  laterality?:
    | "LEFT"
    | "RIGHT"
    | "MIDLINE"
    | "BILATERAL"
    | "NA";
}

export interface AnatomicalTemplate {
  code: string;
  version: number;
  label: string;
  description: string;
  mapType: ClinicalAnatomicalMapType;
  view: AnatomicalTemplateView;
  region: AnatomicalTemplateRegion;
  sex: AnatomicalTemplateSex;
  zones: AnatomicalZone[];
  artwork?: {
    src: string;
    sourceWidth: number;
    sourceHeight: number;
    cropX: number;
    cropY: number;
    cropWidth: number;
    cropHeight: number;
    fullImage?: boolean;
  };
}

const FACE_ZONES: AnatomicalZone[] = [
  { code: "FRONTAL", label: "Frente", laterality: "MIDLINE" },
  { code: "GLABELLA", label: "Glabela", laterality: "MIDLINE" },
  { code: "TEMPORAL_RIGHT", label: "Sien derecha", laterality: "RIGHT" },
  { code: "TEMPORAL_LEFT", label: "Sien izquierda", laterality: "LEFT" },
  { code: "PERIORBITAL_RIGHT", label: "Región periocular derecha", laterality: "RIGHT" },
  { code: "PERIORBITAL_LEFT", label: "Región periocular izquierda", laterality: "LEFT" },
  { code: "MALAR_RIGHT", label: "Región malar derecha", laterality: "RIGHT" },
  { code: "MALAR_LEFT", label: "Región malar izquierda", laterality: "LEFT" },
  { code: "NASAL", label: "Nariz", laterality: "MIDLINE" },
  { code: "NASOLABIAL_RIGHT", label: "Surco nasolabial derecho", laterality: "RIGHT" },
  { code: "NASOLABIAL_LEFT", label: "Surco nasolabial izquierdo", laterality: "LEFT" },
  { code: "PERIORAL", label: "Región perioral", laterality: "MIDLINE" },
  { code: "LIP_UPPER", label: "Labio superior", laterality: "MIDLINE" },
  { code: "LIP_LOWER", label: "Labio inferior", laterality: "MIDLINE" },
  { code: "MARIONETTE_RIGHT", label: "Línea de marioneta derecha", laterality: "RIGHT" },
  { code: "MARIONETTE_LEFT", label: "Línea de marioneta izquierda", laterality: "LEFT" },
  { code: "CHIN", label: "Mentón", laterality: "MIDLINE" },
  { code: "MANDIBULAR_RIGHT", label: "Línea mandibular derecha", laterality: "RIGHT" },
  { code: "MANDIBULAR_LEFT", label: "Línea mandibular izquierda", laterality: "LEFT" },
  { code: "SUBMENTAL", label: "Región submentoniana", laterality: "MIDLINE" },
];

const BODY_FRONT_ZONES: AnatomicalZone[] = [
  { code: "NECK_ANTERIOR", label: "Cuello anterior", laterality: "MIDLINE" },
  { code: "DECOLLETE", label: "Escote", laterality: "MIDLINE" },
  { code: "CHEST_RIGHT", label: "Tórax derecho", laterality: "RIGHT" },
  { code: "CHEST_LEFT", label: "Tórax izquierdo", laterality: "LEFT" },
  { code: "BREAST_RIGHT", label: "Mama derecha", laterality: "RIGHT" },
  { code: "BREAST_LEFT", label: "Mama izquierda", laterality: "LEFT" },
  { code: "UPPER_ABDOMEN", label: "Abdomen superior", laterality: "MIDLINE" },
  { code: "PERIUMBILICAL", label: "Región periumbilical", laterality: "MIDLINE" },
  { code: "LOWER_ABDOMEN", label: "Abdomen inferior", laterality: "MIDLINE" },
  { code: "FLANK_RIGHT", label: "Flanco derecho", laterality: "RIGHT" },
  { code: "FLANK_LEFT", label: "Flanco izquierdo", laterality: "LEFT" },
  { code: "ARM_RIGHT", label: "Brazo derecho", laterality: "RIGHT" },
  { code: "ARM_LEFT", label: "Brazo izquierdo", laterality: "LEFT" },
  { code: "FOREARM_RIGHT", label: "Antebrazo derecho", laterality: "RIGHT" },
  { code: "FOREARM_LEFT", label: "Antebrazo izquierdo", laterality: "LEFT" },
  { code: "THIGH_RIGHT", label: "Muslo derecho", laterality: "RIGHT" },
  { code: "THIGH_LEFT", label: "Muslo izquierdo", laterality: "LEFT" },
  { code: "KNEE_RIGHT", label: "Rodilla derecha", laterality: "RIGHT" },
  { code: "KNEE_LEFT", label: "Rodilla izquierda", laterality: "LEFT" },
];

const BODY_BACK_ZONES: AnatomicalZone[] = [
  { code: "NECK_POSTERIOR", label: "Cuello posterior", laterality: "MIDLINE" },
  { code: "UPPER_BACK", label: "Dorso superior", laterality: "MIDLINE" },
  { code: "BRA_ROLL_RIGHT", label: "Pliegue dorsal derecho", laterality: "RIGHT" },
  { code: "BRA_ROLL_LEFT", label: "Pliegue dorsal izquierdo", laterality: "LEFT" },
  { code: "LOWER_BACK", label: "Región lumbar", laterality: "MIDLINE" },
  { code: "POSTERIOR_FLANK_RIGHT", label: "Flanco posterior derecho", laterality: "RIGHT" },
  { code: "POSTERIOR_FLANK_LEFT", label: "Flanco posterior izquierdo", laterality: "LEFT" },
  { code: "GLUTEAL_RIGHT", label: "Glúteo derecho", laterality: "RIGHT" },
  { code: "GLUTEAL_LEFT", label: "Glúteo izquierdo", laterality: "LEFT" },
  { code: "POSTERIOR_ARM_RIGHT", label: "Brazo posterior derecho", laterality: "RIGHT" },
  { code: "POSTERIOR_ARM_LEFT", label: "Brazo posterior izquierdo", laterality: "LEFT" },
  { code: "POSTERIOR_THIGH_RIGHT", label: "Muslo posterior derecho", laterality: "RIGHT" },
  { code: "POSTERIOR_THIGH_LEFT", label: "Muslo posterior izquierdo", laterality: "LEFT" },
  { code: "POPLITEAL_RIGHT", label: "Región poplítea derecha", laterality: "RIGHT" },
  { code: "POPLITEAL_LEFT", label: "Región poplítea izquierda", laterality: "LEFT" },
];

const LATERAL_ZONES: AnatomicalZone[] = [
  { code: "FACE_LATERAL", label: "Rostro lateral" },
  { code: "NECK_LATERAL", label: "Cuello lateral" },
  { code: "CHEST_LATERAL", label: "Tórax lateral" },
  { code: "FLANK", label: "Flanco" },
  { code: "ARM_LATERAL", label: "Brazo lateral" },
  { code: "HIP", label: "Cadera" },
  { code: "THIGH_LATERAL", label: "Muslo lateral" },
  { code: "KNEE_LATERAL", label: "Rodilla lateral" },
];

const SCALP_ZONES: AnatomicalZone[] = [
  { code: "FRONTAL_SCALP", label: "Cuero cabelludo frontal", laterality: "MIDLINE" },
  { code: "TEMPORAL_SCALP_RIGHT", label: "Región temporal derecha", laterality: "RIGHT" },
  { code: "TEMPORAL_SCALP_LEFT", label: "Región temporal izquierda", laterality: "LEFT" },
  { code: "VERTEX", label: "Vértex", laterality: "MIDLINE" },
  { code: "PARIETAL_RIGHT", label: "Región parietal derecha", laterality: "RIGHT" },
  { code: "PARIETAL_LEFT", label: "Región parietal izquierda", laterality: "LEFT" },
  { code: "OCCIPITAL", label: "Región occipital", laterality: "MIDLINE" },
];

const NOSE_ZONES: AnatomicalZone[] = [
  { code: "RADIX", label: "Radix", laterality: "MIDLINE" },
  { code: "DORSUM", label: "Dorso nasal", laterality: "MIDLINE" },
  { code: "MIDDLE_VAULT", label: "Tercio medio", laterality: "MIDLINE" },
  { code: "SUPRATIP", label: "Suprapunta", laterality: "MIDLINE" },
  { code: "TIP", label: "Punta nasal", laterality: "MIDLINE" },
  { code: "ALAR_RIGHT", label: "Ala nasal derecha", laterality: "RIGHT" },
  { code: "ALAR_LEFT", label: "Ala nasal izquierda", laterality: "LEFT" },
  { code: "NOSTRIL_RIGHT", label: "Narinar derecha", laterality: "RIGHT" },
  { code: "NOSTRIL_LEFT", label: "Narinar izquierda", laterality: "LEFT" },
  { code: "COLUMELLA", label: "Columela", laterality: "MIDLINE" },
  { code: "NASAL_BASE", label: "Base nasal", laterality: "MIDLINE" },
];

type ArtworkCrop = NonNullable<
  AnatomicalTemplate["artwork"]
>;

function artwork(
  src: string,
  sourceWidth: number,
  sourceHeight: number,
  cropX: number,
  cropY: number,
  cropWidth: number,
  cropHeight: number
): ArtworkCrop {
  return {
    src,
    sourceWidth,
    sourceHeight,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
  };
}

const BODY_SHEETS = {
  FEMALE: {
    src: "/assets/clinical/anatomical/body-female-approved.png",
    width: 1536,
    height: 1024,
  },
  MALE: {
    src: "/assets/clinical/anatomical/body-male-approved.png",
    width: 1536,
    height: 1024,
  },
} as const;

const FACE_SHEETS = {
  FEMALE: {
    src: "/assets/clinical/anatomical/face-female-approved.png",
    width: 1942,
    height: 809,
    panelWidth: 388,
  },
  MALE: {
    src: "/assets/clinical/anatomical/face-male-approved.png",
    width: 2172,
    height: 724,
    panelWidth: 434,
  },
} as const;

const NOSE_SHEETS = {
  FEMALE: "/assets/clinical/anatomical/nose-female-approved.png",
  MALE: "/assets/clinical/anatomical/nose-male-approved.png",
} as const;

function bodyTemplate(
  sex: "FEMALE" | "MALE",
  view: "FRONT" | "BACK" | "LEFT" | "RIGHT"
): AnatomicalTemplate {
  const sexLabel =
    sex === "FEMALE" ? "femenino" : "masculino";

  const viewLabel = {
    FRONT: "frente",
    BACK: "dorso",
    LEFT: "lateral izquierdo",
    RIGHT: "lateral derecho",
  }[view];

  const zones =
    view === "FRONT"
      ? BODY_FRONT_ZONES
      : view === "BACK"
        ? BODY_BACK_ZONES
        : LATERAL_ZONES.map((zone) => ({
            ...zone,
            laterality:
              view === "LEFT"
                ? "LEFT" as const
                : "RIGHT" as const,
          }));

  const sheet = BODY_SHEETS[sex];

  return {
    code: `MAUVELORE_BODY_${sex}_${view}_V1`,
    version: 1,
    label: `Cuerpo ${sexLabel} · ${viewLabel}`,
    description:
      "Mapa corporal clínico normalizado para evaluación, planificación y seguimiento.",
    mapType: "BODY",
    view,
    region: "FULL_BODY",
    sex,
    zones,
    artwork:
      view === "FRONT" || view === "BACK"
        ? artwork(
            sheet.src,
            sheet.width,
            sheet.height,
            view === "FRONT" ? 0 : 344,
            0,
            view === "FRONT" ? 344 : 318,
            1024
          )
        : undefined,
  };
}

function bodyDetailTemplate(
  sex: "FEMALE" | "MALE",
  region: "ABDOMEN_FLANKS" | "CHEST_BREAST"
): AnatomicalTemplate {
  const sheet = BODY_SHEETS[sex];
  const sexLabel = sex === "FEMALE" ? "femenino" : "masculino";
  const abdomen = region === "ABDOMEN_FLANKS";

  return {
    code: `MAUVELORE_${abdomen ? "ABDOMEN" : "CHEST"}_${sex}_FRONT_V2`,
    version: 2,
    label: `${abdomen ? "Abdomen" : "Tórax / mamas"} ${sexLabel} · detalle`,
    description: abdomen
      ? "Pared abdominal, flancos, línea media y referencias para planificación quirúrgica."
      : "Mapa torácico ampliado para evaluación y planificación mamaria o pectoral.",
    mapType: "BODY",
    view: "FRONT",
    region,
    sex,
    zones: BODY_FRONT_ZONES,
    artwork: artwork(
      sheet.src,
      sheet.width,
      sheet.height,
      abdomen ? 658 : 1044,
      abdomen ? 40 : 100,
      abdomen ? 386 : 492,
      abdomen ? 944 : 824
    ),
  };
}

const FACE_VIEWS: Array<{
  view: AnatomicalTemplateView;
  label: string;
  panel: number;
}> = [
  { view: "LEFT", label: "perfil izquierdo", panel: 0 },
  { view: "THREE_QUARTER_LEFT", label: "¾ izquierdo", panel: 1 },
  { view: "FRONT", label: "frente", panel: 2 },
  { view: "THREE_QUARTER_RIGHT", label: "¾ derecho", panel: 3 },
  { view: "RIGHT", label: "perfil derecho", panel: 4 },
];

function faceTemplates(
  sex: "FEMALE" | "MALE"
): AnatomicalTemplate[] {
  const sheet = FACE_SHEETS[sex];
  const sexLabel = sex === "FEMALE" ? "femenino" : "masculino";

  return FACE_VIEWS.map((item) => ({
    code: `MAUVELORE_FACE_${sex}_${item.view}_V2`,
    version: 2,
    label: `Rostro ${sexLabel} · ${item.label}`,
    description:
      "Evaluación facial ampliada para diagnóstico, vectores y planificación terapéutica.",
    mapType: "FACE",
    view: item.view,
    region: "FACE",
    sex,
    zones: FACE_ZONES,
    artwork: artwork(
      sheet.src,
      sheet.width,
      sheet.height,
      item.panel * sheet.panelWidth,
      0,
      item.panel === 4
        ? sheet.width - item.panel * sheet.panelWidth
        : sheet.panelWidth,
      sheet.height
    ),
  }));
}

const NOSE_VIEWS: Array<{
  view: AnatomicalTemplateView;
  label: string;
  crop: [number, number, number, number];
}> = [
  { view: "FRONT", label: "frente", crop: [0, 0, 512, 500] },
  { view: "THREE_QUARTER_LEFT", label: "¾ izquierdo", crop: [512, 0, 512, 500] },
  { view: "THREE_QUARTER_RIGHT", label: "¾ derecho", crop: [1024, 0, 512, 500] },
  { view: "LEFT", label: "perfil izquierdo", crop: [0, 500, 260, 524] },
  { view: "INFERIOR", label: "vista basal", crop: [260, 500, 510, 524] },
  { view: "SUPERIOR", label: "vista superior", crop: [770, 500, 510, 524] },
  { view: "RIGHT", label: "perfil derecho", crop: [1280, 500, 256, 524] },
];

function noseTemplates(
  sex: "FEMALE" | "MALE"
): AnatomicalTemplate[] {
  const sexLabel = sex === "FEMALE" ? "femenina" : "masculina";

  return NOSE_VIEWS.map((item) => ({
    code: `MAUVELORE_NOSE_${sex}_${item.view}_V2`,
    version: 2,
    label: `Nariz ${sexLabel} · ${item.label}`,
    description:
      "Mapa nasal ampliado para análisis de eje, dorso, punta, base alar y simetrías.",
    mapType: "FACE",
    view: item.view,
    region: "NOSE",
    sex,
    zones: NOSE_ZONES,
    artwork: artwork(
      NOSE_SHEETS[sex],
      1536,
      1024,
      ...item.crop
    ),
  }));
}


// PREMIUM_ANATOMICAL_V3_CATALOG
// Cada plantilla usa una única lámina aprobada. Las versiones anteriores se
// conservan fuera del selector para poder resolver mapas históricos.
const V3_ASSET_ROOT =
  "/assets/clinical/anatomical/v3";

function fullArtwork(src: string): ArtworkCrop {
  return {
    src,
    sourceWidth: 300,
    sourceHeight: 500,
    cropX: 0,
    cropY: 0,
    cropWidth: 300,
    cropHeight: 500,
    fullImage: true,
  };
}

const EAR_ZONES_BASE = [
  ["HELIX", "Hélix"],
  ["ANTIHELIX", "Antihélix"],
  ["SCAPHA", "Escafa"],
  ["TRIANGULAR_FOSSA", "Fosa triangular"],
  ["CONCHA", "Concha auricular"],
  ["TRAGUS", "Trago"],
  ["ANTITRAGUS", "Antitrago"],
  ["LOBULE", "Lóbulo"],
  ["POSTAURICULAR_SULCUS", "Surco retroauricular"],
  ["MASTOID", "Región mastoidea"],
] as const;

const BODY_V3_VIEWS: Array<{
  view: AnatomicalTemplateView;
  slug: string;
  label: string;
}> = [
  { view: "FRONT", slug: "front", label: "frente" },
  { view: "BACK", slug: "back", label: "dorso" },
  { view: "THREE_QUARTER_LEFT", slug: "three-quarter-left", label: "¾ izquierdo" },
  { view: "THREE_QUARTER_RIGHT", slug: "three-quarter-right", label: "¾ derecho" },
  { view: "LEFT", slug: "profile-left", label: "perfil izquierdo" },
  { view: "RIGHT", slug: "profile-right", label: "perfil derecho" },
];

function bodyV3Templates(sex: "FEMALE" | "MALE"): AnatomicalTemplate[] {
  const sexSlug = sex.toLowerCase();
  const sexLabel = sex === "FEMALE" ? "femenino" : "masculino";
  return BODY_V3_VIEWS.map((item) => {
    const zones = item.view === "BACK"
      ? BODY_BACK_ZONES
      : item.view === "LEFT" || item.view === "RIGHT"
        ? LATERAL_ZONES.map((zone) => ({
            ...zone,
            laterality: item.view === "LEFT" ? "LEFT" as const : "RIGHT" as const,
          }))
        : BODY_FRONT_ZONES;
    return {
      code: `MAUVELORE_BODY_${sex}_${item.view}_V3`,
      version: 3,
      label: `Cuerpo ${sexLabel} · ${item.label}`,
      description: "Mapa corporal clínico individual para evaluación, diseño quirúrgico y planificación terapéutica.",
      mapType: "BODY",
      view: item.view,
      region: "FULL_BODY",
      sex,
      zones,
      artwork: fullArtwork(`${V3_ASSET_ROOT}/body/${sexSlug}/body-${sexSlug}-${item.slug}.png`),
    };
  });
}

const FACE_V3_VIEWS = BODY_V3_VIEWS;

function faceV3Templates(sex: "FEMALE" | "MALE"): AnatomicalTemplate[] {
  const sexSlug = sex.toLowerCase();
  const sexLabel = sex === "FEMALE" ? "femenino" : "masculino";
  return FACE_V3_VIEWS.map((item) => ({
    code: `MAUVELORE_FACE_${sex}_${item.view}_V3`,
    version: 3,
    label: `Rostro ${sexLabel} · ${item.label}`,
    description: "Evaluación facial individual para diagnóstico, vectores, simetrías y planificación terapéutica.",
    mapType: "FACE",
    view: item.view,
    region: "FACE",
    sex,
    zones: FACE_ZONES,
    artwork: fullArtwork(`${V3_ASSET_ROOT}/face/${sexSlug}/face-${sexSlug}-${item.slug}.png`),
  }));
}

const NOSE_V3_VIEWS: Array<{
  view: AnatomicalTemplateView;
  slug: string;
  label: string;
}> = [
  { view: "FRONT", slug: "front", label: "frente" },
  { view: "THREE_QUARTER_LEFT", slug: "three-quarter-left", label: "¾ izquierdo" },
  { view: "THREE_QUARTER_RIGHT", slug: "three-quarter-right", label: "¾ derecho" },
  { view: "LEFT", slug: "profile-left", label: "perfil izquierdo" },
  { view: "RIGHT", slug: "profile-right", label: "perfil derecho" },
  { view: "SUPERIOR", slug: "superior", label: "vista superior" },
  { view: "INFERIOR", slug: "basal", label: "vista basal" },
];

function noseV3Templates(sex: "FEMALE" | "MALE"): AnatomicalTemplate[] {
  const sexSlug = sex.toLowerCase();
  const sexLabel = sex === "FEMALE" ? "femenina" : "masculina";
  return NOSE_V3_VIEWS.map((item) => ({
    code: `MAUVELORE_NOSE_${sex}_${item.view}_V3`,
    version: 3,
    label: `Nariz ${sexLabel} · ${item.label}`,
    description: "Análisis nasal individual de eje, dorso, punta, base alar, proyección y simetrías.",
    mapType: "FACE",
    view: item.view,
    region: "NOSE",
    sex,
    zones: NOSE_ZONES,
    artwork: fullArtwork(`${V3_ASSET_ROOT}/nose/${sexSlug}/nose-${sexSlug}-${item.slug}.png`),
  }));
}

const EAR_V3_VIEWS = [
  { view: "LATERAL", slug: "lateral", label: "lateral" },
  { view: "POSTERIOR", slug: "posterior", label: "posterior" },
  { view: "ANTERIOR_OBLIQUE", slug: "anterior-oblique", label: "oblicua anterior" },
  { view: "POSTERIOR_OBLIQUE", slug: "posterior-oblique", label: "oblicua posterior" },
] as const;

function earV3Templates(sex: "FEMALE" | "MALE"): AnatomicalTemplate[] {
  const sexSlug = sex.toLowerCase();
  const sexLabel = sex === "FEMALE" ? "femenina" : "masculina";
  return (["LEFT", "RIGHT"] as const).flatMap((side) => {
    const sideSlug = side.toLowerCase();
    const sideLabel = side === "LEFT" ? "izquierda" : "derecha";
    const zones: AnatomicalZone[] = EAR_ZONES_BASE.map(([code, label]) => ({
      code: `${code}_${side}`,
      label: `${label} ${sideLabel}`,
      laterality: side,
    }));
    return EAR_V3_VIEWS.map((item) => ({
      code: `MAUVELORE_EAR_${sex}_${side}_${item.view}_V3`,
      version: 3,
      label: `Oreja ${sexLabel} ${sideLabel} · ${item.label}`,
      description: "Mapa auricular individual para evaluación, otoplastia y planificación terapéutica.",
      mapType: "OTHER" as const,
      view: item.view,
      region: "EAR" as const,
      sex,
      zones,
      artwork: fullArtwork(`${V3_ASSET_ROOT}/ear/${sexSlug}/${sideSlug}/ear-${sexSlug}-${sideSlug}-${item.slug}.png`),
    }));
  });
}

const SCALP_V3_VIEWS: Array<{
  view: AnatomicalTemplateView;
  slug: string;
  label: string;
}> = [
  { view: "FRONTAL_HAIRLINE", slug: "frontal-hairline", label: "línea frontal" },
  { view: "SUPERIOR", slug: "superior", label: "vista superior" },
  { view: "VERTEX", slug: "vertex", label: "vértex" },
  { view: "OCCIPITAL", slug: "occipital", label: "occipital" },
  { view: "TEMPORAL_RIGHT", slug: "temporal-right", label: "temporal derecha" },
  { view: "TEMPORAL_LEFT", slug: "temporal-left", label: "temporal izquierda" },
  { view: "ANTERIOR_OBLIQUE_RIGHT", slug: "anterior-oblique-right", label: "oblicua anterior derecha" },
  { view: "ANTERIOR_OBLIQUE_LEFT", slug: "anterior-oblique-left", label: "oblicua anterior izquierda" },
];

function scalpV3Templates(sex: "FEMALE" | "MALE"): AnatomicalTemplate[] {
  const sexSlug = sex.toLowerCase();
  const sexLabel = sex === "FEMALE" ? "femenino" : "masculino";
  return SCALP_V3_VIEWS.map((item) => ({
    code: `MAUVELORE_SCALP_${sex}_${item.view}_V3`,
    version: 3,
    label: `Cuero cabelludo ${sexLabel} · ${item.label}`,
    description: "Mapa capilar individual para diagnóstico de alopecia, densidad y planificación terapéutica.",
    mapType: "SCALP",
    view: item.view,
    region: "SCALP",
    sex,
    zones: SCALP_ZONES,
    artwork: fullArtwork(`${V3_ASSET_ROOT}/scalp/${sexSlug}/scalp-${sexSlug}-${item.slug}.png`),
  }));
}


const LEGACY_ANATOMICAL_TEMPLATES: AnatomicalTemplate[] = [
  bodyTemplate("FEMALE", "FRONT"),
  bodyTemplate("FEMALE", "BACK"),
  bodyTemplate("FEMALE", "LEFT"),
  bodyTemplate("FEMALE", "RIGHT"),
  bodyTemplate("MALE", "FRONT"),
  bodyTemplate("MALE", "BACK"),
  bodyTemplate("MALE", "LEFT"),
  bodyTemplate("MALE", "RIGHT"),
  bodyDetailTemplate("FEMALE", "ABDOMEN_FLANKS"),
  bodyDetailTemplate("FEMALE", "CHEST_BREAST"),
  bodyDetailTemplate("MALE", "ABDOMEN_FLANKS"),
  bodyDetailTemplate("MALE", "CHEST_BREAST"),
  ...faceTemplates("FEMALE"),
  ...faceTemplates("MALE"),
  ...noseTemplates("FEMALE"),
  ...noseTemplates("MALE"),
  {
    code: "MAUVELORE_FACE_FRONT_V1",
    version: 1,
    label: "Rostro · frente",
    description:
      "Evaluación facial integral, diagnóstico, vectores y planificación terapéutica.",
    mapType: "FACE",
    view: "FRONT",
    region: "FACE",
    sex: "NEUTRAL",
    zones: FACE_ZONES,
  },
  {
    code: "MAUVELORE_FACE_LEFT_V1",
    version: 1,
    label: "Rostro · lateral izquierdo",
    description:
      "Perfil facial izquierdo y evaluación del cuello.",
    mapType: "FACE",
    view: "LEFT",
    region: "FACE",
    sex: "NEUTRAL",
    zones: FACE_ZONES.filter(
      (zone) =>
        zone.laterality !== "RIGHT"
    ),
  },
  {
    code: "MAUVELORE_FACE_RIGHT_V1",
    version: 1,
    label: "Rostro · lateral derecho",
    description:
      "Perfil facial derecho y evaluación del cuello.",
    mapType: "FACE",
    view: "RIGHT",
    region: "FACE",
    sex: "NEUTRAL",
    zones: FACE_ZONES.filter(
      (zone) =>
        zone.laterality !== "LEFT"
    ),
  },
  {
    code: "MAUVELORE_SCALP_SUPERIOR_V1",
    version: 1,
    label: "Cuero cabelludo · vista superior",
    description:
      "Mapa capilar para diagnóstico, tratamiento y seguimiento.",
    mapType: "SCALP",
    view: "SUPERIOR",
    region: "SCALP",
    sex: "NEUTRAL",
    zones: SCALP_ZONES,
  },
];

export const ANATOMICAL_TEMPLATES: AnatomicalTemplate[] = [
  ...bodyV3Templates("FEMALE"),
  ...bodyV3Templates("MALE"),
  ...faceV3Templates("FEMALE"),
  ...faceV3Templates("MALE"),
  ...noseV3Templates("FEMALE"),
  ...noseV3Templates("MALE"),
  ...earV3Templates("FEMALE"),
  ...earV3Templates("MALE"),
  ...scalpV3Templates("FEMALE"),
  ...scalpV3Templates("MALE"),
];

export function getAnatomicalTemplate(
  code: string
): AnatomicalTemplate {
  return (
    ANATOMICAL_TEMPLATES.find(
      (template) => template.code === code
    ) ||
    LEGACY_ANATOMICAL_TEMPLATES.find(
      (template) => template.code === code
    ) ||
    ANATOMICAL_TEMPLATES[0]
  );
}
